import React, { FocusEventHandler, useCallback, useEffect, useState } from "react";
import { Modal, Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, IconButton, Icon, TextField, Select, MenuItem } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useSelector } from "react-redux";
import { RootState } from "./store";
import { Provider, RawWebptAvailableCalendar, RawWebptCalendar } from "./types";
import { api } from "./utils/utils";
import { useSnackbar } from "./providers/SnackbarProvider";

//   const { superOrgName, orgName, timezone, phoneNumber, username, password, companyId, clinicId, isSoap2 } = req.body;

const SuperAdmin: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const { showMessage } = useSnackbar();

  const superOrganizations = useSelector((state: RootState) => state.auth.user?.superOrganizations) || [];

  const [creatingOrganization, setCreatingOrganization] = useState(false);
  const [gettingLocations, setGettingLocations] = useState(false);

  const [superOrganizationId, setSuperOrganizationId] = useState("");

  const [superOrgName, setSuperOrgName] = useState("");
  const [locationName, setLocationName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [clinicId, setClinicId] = useState("");
  const [isSoap2, setIsSoap2] = useState(false);

  const [locations, setLocations] = useState<{ companyId: string; facilityId: string; name: string }[]>([]);

  const timezoneOptions = {
    "America/New_York": "Eastern Time (US & Canada)",
    "America/Chicago": "Central Time (US & Canada)",
    "America/Denver": "Mountain Time (US & Canada)",
    "America/Los_Angeles": "Pacific Time (US & Canada)",
    "America/Phoenix": "Mountain Time (Arizona)",
    "Pacific/Honolulu": "Hawaii Time (Hawaii)",
  };

  async function setSuperOrg(id: string) {
    setSuperOrganizationId(id);
    const selectedOrg = superOrganizations?.find((org) => org.superOrganizationId === id);
    if (selectedOrg) {
      setSuperOrgName(selectedOrg.name);
    }
  }

  async function getLocations() {
    if (gettingLocations) {
      console.warn("Already fetching locations");
      return;
    }
    if (!username || !password) {
      showMessage("Please enter WebPT credentials to fetch locations", "error");
      return;
    }
    setGettingLocations(true);
    try {
      if (!user?.token) {
        showMessage("User is not logged in", "error");
        return;
      }
      const payload = { username, password };

      const response = await api.post(`/webpt/locations`, user.token, payload);

      console.log("Fetched locations:", response.data);
      setLocations(response.data);
      setCompanyId(response.data[0]?.companyId || "");
      setClinicId(response.data[0]?.facilityId || "");
      showMessage("Locations fetched successfully", "success");
    } catch (error) {
      console.error("Failed to fetch locations", error);
      showMessage("Failed to fetch locations", "error");
    } finally {
      setGettingLocations(false);
    }
  }

  async function createOrganization() {
    if (creatingOrganization) {
      console.warn("Organization creation already in progress");
      return;
    }
    setCreatingOrganization(true);
    const payload = {
      superOrgName,
      locationName,
      timezone,
      phoneNumber,
      username,
      password,
      companyId,
      clinicId,
      isSoap2,
      ...(superOrganizationId ? { superOrganizationId, newSuperOrg: false } : { newSuperOrg: true }),
    };
    try {
      if (!user?.token) {
        showMessage("User is not logged in", "error");
        return;
      }
      const response = await api.post(`/users/internal/create-organization`, user.token, payload);
      console.log(response.data);
      showMessage("Organization created successfully", "success");
    } catch (error) {
      console.error("Failed to create organization", error);
      showMessage("Failed to create organization", "error");
    } finally {
      setCreatingOrganization(false);
    }
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Super Admin Panel
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 500 }}>
          <TextField label="Organization Name" value={superOrgName} onChange={(e) => setSuperOrgName(e.target.value)} fullWidth />
          <TextField label="Location Name (Optional)" value={locationName} onChange={(e) => setLocationName(e.target.value)} fullWidth />
          <Typography variant="subtitle1">Timezone</Typography>
          <Select label="Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} fullWidth>
            {Object.entries(timezoneOptions).map(([tz, label]) => (
              <MenuItem key={tz} value={tz}>
                {label}
              </MenuItem>
            ))}
          </Select>
          <TextField label="Phone Number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} fullWidth />
          <TextField label="WebPT Username" value={username} onChange={(e) => setUsername(e.target.value)} fullWidth />
          <TextField label="WebPT Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <input type="checkbox" checked={isSoap2} onChange={(e) => setIsSoap2(e.target.checked)} />
            <Typography>Use SOAP 2.0 (i.e. Clicking on a patient takes you to emr.webpt.com)</Typography>
          </Box>
          <Button variant="contained" color="secondary" onClick={getLocations} disabled={gettingLocations}>
            {gettingLocations ? "Fetching..." : "Fetch Locations (Note: this will kick you out of webpt)"}
          </Button>
          {locations.length > 0 && (
            <Box sx={{ border: "1px solid #ccc", borderRadius: 1, p: 1 }}>
              <Typography variant="subtitle1">Available Locations:</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Company ID</TableCell>
                    <TableCell>Clinic ID</TableCell>
                    <TableCell>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {locations.map((loc) => (
                    <TableRow
                      key={`${loc.companyId}-${loc.facilityId}-${loc.name}`}
                      hover
                      onClick={() => {
                        setCompanyId(loc.companyId);
                        setClinicId(loc.facilityId);
                      }}
                      selected={companyId === loc.companyId && clinicId === loc.facilityId}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{loc.companyId}</TableCell>
                      <TableCell>{loc.facilityId}</TableCell>
                      <TableCell>{loc.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          <TextField label="Company ID" value={companyId} onChange={(e) => setCompanyId(e.target.value)} fullWidth />
          <TextField label="Clinic ID" value={clinicId} onChange={(e) => setClinicId(e.target.value)} fullWidth />
          <Button variant="contained" color="primary" onClick={createOrganization} disabled={creatingOrganization}>
            {creatingOrganization ? "Creating..." : "Create Organization"}
          </Button>
        </Box>
        <Box>
          <Typography variant="subtitle1">Select Org (Only use if adding a location to existing org)</Typography>
          <Select label="Add to existing organization?" value={superOrganizationId} onChange={(e) => setSuperOrg(e.target.value)} fullWidth>
            {superOrganizations.map((org) => (
              <MenuItem key={org.superOrganizationId} value={org.superOrganizationId}>
                {org.name}
              </MenuItem>
            ))}
            <MenuItem key="" value="">
              Create New Super Organization
            </MenuItem>
          </Select>
        </Box>
      </Box>
    </Box>
  );
};

export default SuperAdmin;
